import { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import { Link, useParams } from "react-router-dom";
import apiClient from "../../api-services/ApiClient";
import Preloader from "../../app-components/preloader/Preloader";
import SafeHtmlComponent from "../../app-components/safehtml/SafeHtmlComponent";
import {
  DefectCriteriaInterface,
  PrioritiesInterface,
  SegmentInterface,
  SegmentQuestionInterface,
} from "../../interface-and-types/inspection.schema";
import { LuArrowLeft, LuInfo } from "react-icons/lu";
import { SITELINKS } from "../../app-routes/links";

const VOViewInServiceInspection = () => {
  const { id: inspectionFormId } = useParams();
  const [loading, setLoading] = useState(false);
  const [vesselName, setVesselName] = useState(null);
  const [currentSegment, setCurrentSegment] = useState<number>(22);
  const [vesselStatus, setVesselStaus] = useState(null);
  const [priorities, setPriorities] = useState<PrioritiesInterface[]>([]);
  const [defectType, setDefectType] = useState<any[]>([]);

  const [segment, setSegment] = useState<SegmentInterface[]>([]);

  const [criticalEquipmentTypes, setCriticalEquipmentTypes] = useState<any[]>(
    []
  );

  const [defectFormRows, setDefectFormRows] = useState<
    DefectCriteriaInterface[]
  >([]);

  const [equipmentList, setEquipmentList] = useState<any[]>([]);

  useEffect(() => {
    getInspectionById();
    getPriorities();
    getSegment();
    getCriticalEquipmentTypes();
    getDefectType();
    getEquipmentList();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (currentSegment !== null) {
      getInspectionFormSegmentDefect(segment);
    }
    // eslint-disable-next-line
  }, [currentSegment]);

  const handleInputChange = <T extends keyof SegmentQuestionInterface>(
    index: number,
    field: T,
    value: SegmentQuestionInterface[T]
  ) => {
    const updatedSegments = [...segment];

    // Get the current segment and ensure it exists
    const currentSegmentInfo = updatedSegments[currentSegment];
    if (!currentSegmentInfo) {
      console.error("Current segment not found.");
      alert("info not accurate");
      return;
    }

    // Ensure segmentQuestions is an array and make a copy of it
    const currentSegmentQuestions = [
      ...(currentSegmentInfo.segmentQuestions || []),
    ];

    // Check if the specified question exists in the array
    const currentQuestionInSegment = currentSegmentQuestions[index];
    if (!currentQuestionInSegment) {
      console.error(`Question at index ${index} not found in segment.`);
      alert("info not accurate");
      return;
    }

    // Update the specific field in the question
    const updatedQuestion = { ...currentQuestionInSegment, [field]: value };

    // Replace the old question with the updated one
    currentSegmentQuestions[index] = updatedQuestion;

    // Update the segment with the modified questions
    updatedSegments[currentSegment] = {
      ...currentSegmentInfo, // Preserve other properties
      segmentQuestions: currentSegmentQuestions,
    };

    // Update the state
    setSegment(updatedSegments);
  };

  const handleDefectFormInputChange = <T extends keyof DefectCriteriaInterface>(
    index: number,
    field: T,
    value: DefectCriteriaInterface[T]
  ) => {
    const newRows = [...defectFormRows];
    newRows[index][field] = value;
    setDefectFormRows(newRows);
  };

  // Function to convert a single file to Base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Function to handle file selection
  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const files = event.target.files;
    const newRows = [...defectFormRows];

    if (files) {
      const filesArray = Array.from(files);

      const base64Promises = filesArray.map(async (file) => {
        const base64 = await fileToBase64(file);
        return {
          base64Image: base64,
          mimeType: file.type,
        };
      });

      // Convert all files to Base64
      Promise.all(base64Promises).then((base64Arr: any) => {
        newRows[index]["defectCheckListAttachments"] = base64Arr;
        setDefectFormRows(newRows);
      });
    }
  };

  // getSegment
  const getInspectionById = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get(
        `InspectionForm/${inspectionFormId}/User`
      );
      setVesselName(res.data?.data?.vessel?.name);
      setVesselStaus(res.data?.data?.formState);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getSegment
  const getSegment = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Segment");
      const data = res.data.data;
      const sorted = data.sort(
        (a: any, b: any) => a.segmentNumber - b.segmentNumber
      );
      setSegment(sorted);
      await getInspectionFormSegmentDefect(sorted);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // get the user filled details
  const getInspectionFormSegmentDefect = async (segmentResponse: any) => {
    setLoading(true);

    try {
      const segmentInfo = segmentResponse[currentSegment];

      if (!segmentInfo) {
        console.error("Current segment not found.");
        return;
      }

      const segmentId = segmentInfo.id;

      // Fetch inspection form data
      const res: any = await apiClient.get(
        `InspectionForm/${inspectionFormId}/FormSegment/${segmentId}/Section`
      );

      const inspectionFormSegment =
        res.data?.data?.inspectionFormSegments ?? [];

      const segmentQuestions: any = segmentInfo.segmentQuestions || [];

      let updatedSegmentQuestions = [];
      if (segmentQuestions.length > 0) {
        updatedSegmentQuestions = segmentQuestions.map((item: any) => {
          const matchingSegment = inspectionFormSegment.find(
            (seg: any) => seg.questionNumber === item.questionNumber
          );

          return {
            ...item,
            answer: matchingSegment?.suitabilityStatus || "", // Use fallback if not found
            comment: matchingSegment?.commentsOrFinds || null,
            priority: matchingSegment?.priority || null,
          };
        });
      } else {
        updatedSegmentQuestions = inspectionFormSegment.map((item: any) => {
          return {
            ...item,
            answer: item?.suitabilityStatus || "", // Use fallback if not found
            comment: item?.commentsOrFinds || null,
            priority: item?.priority || null,
          };
        });
      }

      segmentInfo.segmentQuestions = updatedSegmentQuestions;
      segmentResponse[currentSegment] = segmentInfo;

      const vesselDefectCheckLists =
        res.data?.data?.vesselDefectCheckLists ?? [];
      console.log(vesselDefectCheckLists);

      let updatedVesselDefectCheckLists = [];
      if (vesselDefectCheckLists.length > 0) {
        updatedVesselDefectCheckLists = vesselDefectCheckLists.map(
          (item: any) => {
            return {
              ...item,
              equipmentName: item?.equipmentName || "",
              equipmentId: item?.equipment?.id || "",
              criticalEquipment:
                item?.criticalEquipment === "Yes"
                  ? "1"
                  : item?.criticalEquipment === "No"
                  ? "2"
                  : null,
              defectTypeId: item?.defectTypeId || null,
              comments: item?.comments || null,
              priority: item?.priorityId || null,
              defectCheckListAttachmentsList:
                item?.defectCheckListAttachments || [],
            };
          }
        );
      }

      setDefectFormRows(updatedVesselDefectCheckLists);

      setSegment(segmentResponse);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getPriorities
  const getPriorities = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/Priorities");
      setPriorities(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getCriticalEquipmentTypes
  const getCriticalEquipmentTypes = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/CriticalEquipmentTypes");
      setCriticalEquipmentTypes(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getCriticalEquipmentTypes
  const getEquipmentList = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Equipment");
      setEquipmentList(res.data.data);
    } catch (error) {
      console.error("Error fetching", error);
    } finally {
      setLoading(false);
    }
  };

  // getDefectType
  const getDefectType = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("DefectType");
      setDefectType(res.data.data.filter((item: any) => !item.isDeleted));
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const prevPage = async () => {
    await setCurrentSegment(currentSegment - 1);
  };

  const nextPage = async () => {
    await setCurrentSegment(currentSegment + 1);
  };

  const sectionDropdown = async (page: any) => {
    await setCurrentSegment(page);
  };

  const downloadFile = async () => {
    try {
      setLoading(true);
      await apiClient
        .get(`InspectionForm/${inspectionFormId}/Download`)
        .then((res) => {
          console.log(res);
          var a = document.createElement("a");
          a.href = "data:image/png;base64," + res.data.data.base64String;
          a.download = res.data.data.fileName; // res.data.data.attachmentType.name+'-'+res.data.data.name
          a.click();

          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-3 pb-24 flex justify-between items-center w-full">
          {/* page-name / form name */}
          <div className="font-medium lg:text-sm text-gray-100 flex gap-3 items-center">
            <Link to={SITELINKS.VO_INSPECTION} className="text-[20px]">
              <LuArrowLeft />
            </Link>
            <h4>
              Assessment Form / <span>{vesselName}</span>
            </h4>
          </div>

          <div className="flex gap-1">
            <button
              className="disabled:bg-gray-400 flex gap-2 items-center justify-start text-xs bg-slate-500 border-slate-400 shadow-sm p-1 border-2 rounded text-white font-medium px-2 hover:bg-secondaryBlue transition-colors duration-300 ease-in-out transform hidden"
              type="button"
              onClick={prevPage}
              disabled={currentSegment === 0}
            >
              <FaChevronLeft className="text-md" />
              <span>Back</span>
            </button>

            <button
              className="disabled:bg-gray-400 flex gap-2 items-center text-xs bg-slate-500 shadow p-1 border-2 border-slate-400 rounded text-white font-medium px-2 hover:bg-secondaryBlue transition-colors duration-300 ease-in-out transform hidden"
              type="button"
              onClick={nextPage}
              disabled={segment.length - 1 === currentSegment}
            >
              <span>Next</span>
              <FaChevronRight className="text-md" />
            </button>

            <div>
              {vesselStatus === "Completed" && (
                <button
                  className="flex gap-1 items-center text-xs bg-green-600 shadow p-1 border-2 rounded text-white font-medium px-2"
                  type="button"
                  onClick={downloadFile}
                >
                  <span>FInal PDF</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* page content */}
      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-105px]">
        <div className="rounded-xl bg-white border border-gray-200 mb-5 hidden">
          <div className="p-4 2xl:p-6 pb-8">
            {/* page / segment lead area */}
            <div className="flex justify-between text-slate-800 items-center mb-4 font-medium">
              <h1 className="font-semibold text-sm text-gray-800">
                {segment[currentSegment]?.name}{" "}
                <small className="text-orange-600 text-[9px]">
                  (*) All fields are required!
                </small>
              </h1>

              <select
                id="array-length-dropdown"
                className="text-xs border p-1 rounded"
                value={currentSegment}
                onChange={(e) => sectionDropdown(Number(e.target.value))}
              >
                {Array.from({ length: segment.length }, (_, index) => (
                  <option key={index} value={index}>
                    Section {index + 1} - {segment[index]?.name}
                  </option>
                ))}
              </select>

              <span className="bg-gray-200 text-xs rounded-full px-1">
                {currentSegment + 1} of {segment?.length}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="w-full bg-gray-200 text-gray-600 capitalize text-xs leading-normal text-left font-semibold">
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      SN
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      {segment[currentSegment]?.name}
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      Yes
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      No
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      N/A
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      Comments
                    </th>
                    <th className="border border-gray-300 px-2 py- text-slate-800">
                      Priority
                    </th>
                  </tr>
                </thead>

                <tbody className="text-gray-800 text-xs font-normal">
                  {segment[currentSegment]?.segmentQuestions.map(
                    (row, index) => (
                      <tr key={index} className="even:bg-gray-50">
                        <td className="border border-gray-300 px-2 py-2">
                          {index + 1}
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          <SafeHtmlComponent html={row.question} />
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          <input
                            className="h-[1.125rem] w-[1.125rem] rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300"
                            type="checkbox"
                            checked={
                              row?.answer?.toUpperCase() === "YES" || false
                            }
                            onChange={(e) =>
                              handleInputChange(index, "answer", "YES")
                            }
                            disabled
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          <input
                            className="h-[1.125rem] w-[1.125rem] rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300"
                            type="checkbox"
                            checked={
                              row?.answer?.toUpperCase() === "NO" || false
                            }
                            onChange={(e) =>
                              handleInputChange(index, "answer", "NO")
                            }
                            disabled
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          <input
                            className="h-[1.125rem] w-[1.125rem] rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300"
                            type="checkbox"
                            checked={
                              row?.answer?.toUpperCase() === "NA" || false
                            }
                            onChange={(e) =>
                              handleInputChange(index, "answer", "NA")
                            }
                            disabled
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          <input
                            type="text"
                            className="min-w-[300px] w-full border p-1 rounded"
                            value={row?.comment || ""}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "comment",
                                e.target.value
                              )
                            }
                            placeholder="Add comments"
                            disabled
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-1">
                          <select
                            className="w-full border py-1 hover:border-slate-500 rounded p-1 w-[100px]"
                            value={row?.priority || ""}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "priority",
                                e.target.value
                              )
                            }
                            disabled
                          >
                            <option value="">Select</option>
                            {priorities.map((priority, i) => {
                              return (
                                <option key={i} value={priority?.value}>
                                  {priority?.name} ({priority?.value})
                                </option>
                              );
                            })}
                          </select>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Defect form criteria */}
        <div className="rounded-lg bg-white border border-gray-200 mb-10">
          <div className="p-4">
            <div className="flex justify-between text-slate-800 items-center mb-4 font-medium">
              <h1 className="text-sm">Defect Form Criteria</h1>
            </div>

            <div className="overflow-x-auto">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-200 text-left text-xs">
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800  align-top">
                      SN
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800  align-top">
                      Equipment Name <b className="text-red-600">*</b>
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800  align-top">
                      Critical Equipment
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Type of Defect
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Comments
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      <span className="flex gap-2">
                        <span>Priority </span>{" "}
                        <LuInfo
                          className="text-orange-600"
                          title="Priority 0 (P0): Urgent actions related to critical defects requiring immediate closure.
  Priority 1 (P1): High-priority actions related to major defects with a closure timeframe of 48 hours to 5 days. – we are to disregard the timeframe in this priority list and retain allowing the Vessel Operator input due date. The due date can however be overridden by the Marine Superintendent with a comment to the Vessel Operator.
  Priority 2 (P2): Medium priority actions related to minor defects with a closure timeframe of, for example, 14 days.
  Priority 3 (P3): Low-priority actions related to routine maintenance and general improvements not necessarily linked to vessel compliance."
                        />
                      </span>
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Attach Picture
                    </th>
                  </tr>
                </thead>

                <tbody className="text-xs">
                  {defectFormRows.map((row, index) => (
                    <tr key={index} className="even:bg-gray-50">
                      <td className="border border-gray-300 px-2 py-1 align-top">
                        {index + 1}
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        {/* <input
                          type="text"
                          className="w-full min-w-[240px] border  p-1.5"
                          value={row.equipmentName}
                          disabled
                          onChange={(e) =>
                            handleDefectFormInputChange(
                              index,
                              "equipmentName",
                              e.target.value
                            )
                          }
                          placeholder="Enter Equipment Name"
                        /> */}

                        <select
                          className="w-full border py-1 hover:border-slate-500 rounded p-1"
                          value={row?.equipmentId || ""}
                          onChange={(e) =>
                            handleDefectFormInputChange(
                              index,
                              "equipmentId",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select</option>
                          {equipmentList.map((item, i) => {
                            return (
                              <option key={i} value={item?.id}>
                                {item?.name}
                              </option>
                            );
                          })}
                        </select>
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        <select
                          className="w-full border hover:border-slate-500 rounded p-1 py-1.5"
                          value={row?.criticalEquipment || ""}
                          disabled
                          onChange={(e) =>
                            handleDefectFormInputChange(
                              index,
                              "criticalEquipment",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select</option>
                          {criticalEquipmentTypes.map((item, i) => {
                            return (
                              <option key={i} value={item?.id}>
                                {item?.name}
                              </option>
                            );
                          })}
                        </select>
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        <select
                          className="w-full border py-1.5 hover:border-slate-500 rounded p-1.5"
                          value={row?.defectTypeId || ""}
                          disabled
                          onChange={(e) =>
                            handleDefectFormInputChange(
                              index,
                              "defectTypeId",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select</option>
                          {defectType.map((item, i) => {
                            return (
                              <option key={i} value={item?.id}>
                                {item?.name}
                              </option>
                            );
                          })}
                        </select>
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        <input
                          className="min-w-[300px] w-full border p-1.5 "
                          value={row?.comments || ""}
                          disabled
                          onChange={(e) =>
                            handleDefectFormInputChange(
                              index,
                              "comments",
                              e.target.value
                            )
                          }
                          placeholder="Add comments"
                        />
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        <select
                          className="w-full border py-1.5 hover:border-slate-500 rounded p-1.5 w-[100px]"
                          value={row?.priority || ""}
                          disabled
                          onChange={(e) =>
                            handleDefectFormInputChange(
                              index,
                              "priority",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select</option>
                          {priorities.map((priority, i) => {
                            return (
                              <option key={i} value={priority?.id}>
                                {priority?.name}
                              </option>
                            );
                          })}
                        </select>
                      </td>

                      <td className="border border-gray-300 px-2 py-1 w-[160px] align-top">
                        <input
                          type="file"
                          id="file-upload"
                          multiple
                          accept="image/*"
                          className="border p-1.5 w-[160px] hidden"
                          onChange={(e) => handleFileChange(e, index)}
                          disabled
                        />
                        <div className="flex gap-2 flex-grow mt-1">
                          {/* {row?.defectCheckListAttachments.map(
                            (item: any, index: number) => (
                              <img
                                alt="attachment"
                                src={item.base64Image}
                                key={index}
                                className="pb-1 h-[40px] rounded bg-slate-50"
                              />
                            )
                          )} */}

                          {row?.defectCheckListAttachments.map(
                            (item: any, index: number) => (
                              <img
                                alt="ItemAttached"
                                src={
                                  item.base64Image.includes("data:image/")
                                    ? item.base64Image
                                    : `data:image/png;base64,${item.base64Image}`
                                }
                                key={index}
                                className="pb-1 h-[40px] rounded bg-slate-50"
                                title={item.base64Image}
                              />
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {defectFormRows.length === 0 && (
                <div className="p-10 text-center text-slate-600 text-xs border">
                  No record
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Preloader loading={loading} />
    </>
  );
};

export default VOViewInServiceInspection;
